/**
 * Edge Generation Utilities
 *
 * Derives display edges from mission unlockConditions, then reroutes through
 * passive milestone markers attached to host missions (fan-in / fan-out).
 */

import { NODE_WIDTH, NODE_HEIGHT } from '../constants';
import {
  isMilestoneNode,
  buildMilestonesByHostMap,
  getMilestoneLayoutDims,
  getMilestoneConnectionGeometry,
  getMilestoneLabelSide,
  getMilestoneAnchor,
} from '../milestoneUtils';
import { calculateOptimalConnection, getBaseConnectionPoint } from './connectionPoints';
import { getAllConditionEntries, getDependencyTaskId, getDependencyBranch } from '../../../shared/ConditionsDropdown/utils/conditionHelpers';
import { getNoteAnchor, isNoteNode } from '../noteUtils';

/** Footprint dims — used for obstacle avoidance so other lines route around the label. */
function getObstacleDims(nodeDimensions, taskId, task) {
  if (task?.nodeType === 'milestone') {
    return getMilestoneLayoutDims(nodeDimensions, taskId);
  }
  return nodeDimensions[taskId] || { width: NODE_WIDTH, height: NODE_HEIGHT };
}

/**
 * Connection geometry for a node. Milestones collapse to a tiny junction box at the
 * junction edge of the footprint so every dependency line converges on one point.
 */
function getConnectionGeometry(nodePositions, nodeDimensions, taskId, task, taskMap) {
  const layoutPos = nodePositions[taskId];
  if (task?.nodeType === 'milestone') {
    const layoutDims = getMilestoneLayoutDims(nodeDimensions, taskId);
    const anchor = getMilestoneAnchor(task);
    const host = anchor ? taskMap.get(anchor.hostTaskId) : null;
    const hostPos = host ? nodePositions[host.id] : null;
    const hostDims = host ? nodeDimensions[host.id] : null;
    const side = getMilestoneLabelSide(layoutPos, layoutDims, hostPos, hostDims);
    return getMilestoneConnectionGeometry(layoutPos, layoutDims, side);
  }
  return {
    pos: layoutPos,
    dims: nodeDimensions[taskId] || { width: NODE_WIDTH, height: NODE_HEIGHT },
  };
}

/**
 * Get upstream tasks for a task - from unlockConditions (v1 or v2) or fallback to dependsOn
 * Returns array of { taskId, branch } objects
 */
export function getUpstreamTasks(task, allTasks = null) {
  const entries = getAllConditionEntries(task.unlockConditions, allTasks);
  if (entries.length > 0) {
    const seen = new Set();
    return entries.filter(e => {
      if (seen.has(e.taskId)) return false;
      seen.add(e.taskId);
      return true;
    });
  }
  const deps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
  return deps.map(dep => ({
    taskId: getDependencyTaskId(dep),
    branch: getDependencyBranch(dep),
  })).filter(d => d.taskId);
}

/**
 * Build logical dependency pairs from mission/decision unlock conditions only.
 * @returns {Array<{ from: string, to: string, branch: string|null }>}
 */
export function buildLogicalEdgePairs(tasks) {
  const taskMap = new Map(tasks.map((t) => [t.id, t]));
  const pairs = [];

  tasks.forEach((task) => {
    if (isMilestoneNode(task) || task.nodeType === 'note') return;
    getUpstreamTasks(task, tasks).forEach(({ taskId: depId, branch }) => {
      if (!depId || !taskMap.has(depId)) return;
      const depTask = taskMap.get(depId);
      if (isMilestoneNode(depTask) || depTask?.nodeType === 'note') return;
      pairs.push({ from: depId, to: task.id, branch: branch || null });
    });
  });

  return pairs;
}

/**
 * Expand logical edges through milestone pass-through nodes.
 * Hosts with no incoming/outgoing mission still get a stub line to their
 * attached milestone so last-in-chain (and first-in-chain) markers stay connected.
 * @returns {Array<{ from: string, to: string, branch: string|null, isMilestoneStub?: boolean }>}
 */
export function expandEdgesThroughMilestones(tasks) {
  const { inputByHost, outputByHost } = buildMilestonesByHostMap(tasks);
  const logical = buildLogicalEdgePairs(tasks);
  const segmentKeys = new Set();
  const segments = [];

  const addSegment = (from, to, branch, extra = {}) => {
    const key = `${from}->${to}${branch ? `-${branch}` : ''}`;
    if (segmentKeys.has(key)) return;
    segmentKeys.add(key);
    segments.push({ from, to, branch, ...extra });
  };

  logical.forEach(({ from, to, branch }) => {
    const chain = [from];

    const outputMs = outputByHost.get(from);
    if (outputMs?.length) {
      outputMs.forEach((m) => chain.push(m.id));
    }

    const inputMs = inputByHost.get(to);
    if (inputMs?.length) {
      inputMs.forEach((m) => chain.push(m.id));
    }

    chain.push(to);

    for (let i = 0; i < chain.length - 1; i += 1) {
      addSegment(chain[i], chain[i + 1], i === 0 ? branch : null);
    }
  });

  const hostsWithOutgoing = new Set(logical.map((pair) => pair.from));
  const hostsWithIncoming = new Set(logical.map((pair) => pair.to));

  outputByHost.forEach((milestones, hostId) => {
    if (hostsWithOutgoing.has(hostId) || !milestones.length) return;
    const chain = [hostId, ...milestones.map((m) => m.id)];
    for (let i = 0; i < chain.length - 1; i += 1) {
      addSegment(chain[i], chain[i + 1], null, { isMilestoneStub: true });
    }
  });

  inputByHost.forEach((milestones, hostId) => {
    if (hostsWithIncoming.has(hostId) || !milestones.length) return;
    const chain = [...milestones.map((m) => m.id), hostId];
    for (let i = 0; i < chain.length - 1; i += 1) {
      addSegment(chain[i], chain[i + 1], null, { isMilestoneStub: true });
    }
  });

  return segments;
}

/**
 * Host→note display stems. Id matches ELK (`${host}-${note}-note-anchor`) so they
 * reuse the same spaced orthogonal path as sibling mission edges.
 */
export function appendNoteStemSegments(tasks, segments = []) {
  const knownIds = new Set((tasks || []).map((task) => task.id));
  const used = new Set(segments.map((seg) => `${seg.from}->${seg.to}`));

  (tasks || []).forEach((task) => {
    if (!isNoteNode(task)) return;
    const hostId = getNoteAnchor(task)?.hostTaskId;
    if (!hostId || !knownIds.has(hostId)) return;
    const key = `${hostId}->${task.id}`;
    if (used.has(key)) return;
    used.add(key);
    segments.push({
      from: hostId,
      to: task.id,
      branch: null,
      isNoteStem: true,
    });
  });

  return segments;
}

/**
 * Calculate the target connection point for an edge
 */
function calculateTargetPoint(otherPos, otherDims, otherSide) {
  const otherWidth = otherDims?.width || NODE_WIDTH;
  const otherHeight = otherDims?.height || NODE_HEIGHT;

  if (otherSide === 'top') {
    return { targetX: otherPos.x + otherWidth / 2, targetY: otherPos.y };
  } if (otherSide === 'bottom') {
    return { targetX: otherPos.x + otherWidth / 2, targetY: otherPos.y + otherHeight };
  } if (otherSide === 'left') {
    return { targetX: otherPos.x, targetY: otherPos.y + otherHeight / 2 };
  }
  return { targetX: otherPos.x + otherWidth, targetY: otherPos.y + otherHeight / 2 };
}

function findOptimalAssignmentPermutation(connections, connectionTargets, offsetPositions, baseX, baseY, side, edgesWithSides) {
  const indexed = connectionTargets.map((t, i) => ({ ...t, origIdx: i }));

  if (side === 'top' || side === 'bottom') {
    indexed.sort((a, b) => a.targetX - b.targetX);
  } else {
    indexed.sort((a, b) => a.targetY - b.targetY);
  }

  const sortedOffsets = [...offsetPositions].sort((a, b) => a - b);

  for (let i = 0; i < indexed.length; i += 1) {
    const offset = sortedOffsets[i];
    const conn = indexed[i].conn;
    const edge = edgesWithSides[conn.idx];

    if (conn.type === 'from') {
      edge.fromOffset = offset;
    } else {
      edge.toOffset = offset;
    }
  }
}

function findOptimalAssignmentGreedy(connectionTargets, offsetPositions, baseX, baseY, side, edgesWithSides) {
  const indexed = connectionTargets.map((t, i) => ({ ...t, origIdx: i }));

  if (side === 'top' || side === 'bottom') {
    indexed.sort((a, b) => a.targetX - b.targetX);
  } else {
    indexed.sort((a, b) => a.targetY - b.targetY);
  }

  const sortedOffsets = [...offsetPositions].sort((a, b) => a - b);

  for (let i = 0; i < indexed.length; i += 1) {
    const offset = sortedOffsets[i];
    const edge = edgesWithSides[indexed[i].conn.idx];

    if (indexed[i].conn.type === 'from') {
      edge.fromOffset = offset;
    } else {
      edge.toOffset = offset;
    }
  }
}

function assignSequentialOffsets(connections, offsetSpacing, edgesWithSides) {
  const totalWidth = (connections.length - 1) * offsetSpacing;
  const startOffset = -totalWidth / 2;

  connections.forEach((conn, i) => {
    const offset = startOffset + i * offsetSpacing;
    const edge = edgesWithSides[conn.idx];
    if (conn.type === 'from') {
      edge.fromOffset = offset;
    } else {
      edge.toOffset = offset;
    }
  });
}

function calculateConnectionOffsets(connectionCounts, nodePositions, nodeDimensions, edgesWithSides) {
  const OFFSET_SPACING = 15;

  connectionCounts.forEach((connections, key) => {
    if (connections.length <= 1) return;

    const lastDashIdx = key.lastIndexOf('-');
    const nodeId = key.substring(0, lastDashIdx);
    const side = key.substring(lastDashIdx + 1);

    const nodePos = nodePositions[nodeId];
    const nodeDims = nodeDimensions[nodeId] || { width: NODE_WIDTH, height: NODE_HEIGHT };

    if (!nodePos) {
      assignSequentialOffsets(connections, OFFSET_SPACING, edgesWithSides);
      return;
    }

    const { baseX, baseY } = getBaseConnectionPoint(nodePos, nodeDims, side);

    const totalWidth = (connections.length - 1) * OFFSET_SPACING;
    const startOffset = -totalWidth / 2;
    const offsetPositions = connections.map((_, i) => startOffset + i * OFFSET_SPACING);

    const connectionTargets = connections.map(conn => {
      const edge = edgesWithSides[conn.idx];
      const otherPos = conn.type === 'from' ? edge.toPos : edge.fromPos;
      const otherDims = conn.type === 'from' ? edge.toDims : edge.fromDims;
      const otherSide = conn.type === 'from' ? edge.toSide : edge.fromSide;

      const { targetX, targetY } = calculateTargetPoint(otherPos, otherDims, otherSide);
      return { targetX, targetY, conn };
    });

    const n = connections.length;

    if (n <= 6) {
      findOptimalAssignmentPermutation(connections, connectionTargets, offsetPositions, baseX, baseY, side, edgesWithSides);
    } else {
      findOptimalAssignmentGreedy(connectionTargets, offsetPositions, baseX, baseY, side, edgesWithSides);
    }
  });
}

/**
 * Generate display edges with milestone pass-through routing.
 */
export function generateEdges(tasks, nodePositions, nodeDimensions = {}) {
  const taskMap = new Map(tasks.map(t => [t.id, t]));

  // Footprint boxes for obstacle avoidance (milestones reserve room for their side label).
  const allNodes = tasks.map(task => ({
    id: task.id,
    pos: nodePositions[task.id],
    dims: getObstacleDims(nodeDimensions, task.id, task),
  })).filter(n => n.pos);

  // Connection geometry per node — milestones collapse to the junction point.
  const connPositions = {};
  const connDims = {};
  tasks.forEach((task) => {
    if (!nodePositions[task.id]) return;
    const geom = getConnectionGeometry(nodePositions, nodeDimensions, task.id, task, taskMap);
    connPositions[task.id] = geom.pos;
    connDims[task.id] = geom.dims;
  });

  const segments = appendNoteStemSegments(tasks, expandEdgesThroughMilestones(tasks));
  const edgesWithSides = [];
  let edgeIndex = 0;

  segments.forEach(({ from: depId, to: targetId, branch, isMilestoneStub, isNoteStem }) => {
    const fromPos = connPositions[depId];
    const toPos = connPositions[targetId];
    const fromTask = taskMap.get(depId);
    const toTask = taskMap.get(targetId);

    if (!fromPos || !toPos || !fromTask || !toTask) return;

    if (!Number.isFinite(fromPos.x) || !Number.isFinite(fromPos.y)
        || !Number.isFinite(toPos.x) || !Number.isFinite(toPos.y)) {
      return;
    }

    const fromDims = connDims[depId];
    const toDims = connDims[targetId];

    const { fromSide, toSide } = calculateOptimalConnection(fromPos, toPos, fromDims, toDims);

    let branchColor = null;
    let branchLabel = branch;
    if (fromTask?.nodeType === 'decision' && branch) {
      const options = fromTask.options || [
        { id: 'yes', label: 'Yes', color: '#10b981' },
        { id: 'no', label: 'No', color: '#ef4444' },
      ];
      const option = options.find(opt => opt.id === branch);
      if (option) {
        branchColor = option.color;
        branchLabel = option.label;
      }
    }

    edgesWithSides.push({
      id: isNoteStem
        ? `${depId}-${targetId}-note-anchor`
        : `${depId}-${targetId}${branch ? `-${branch}` : ''}`,
      from: depId,
      to: targetId,
      fromPos: { x: fromPos.x, y: fromPos.y },
      toPos: { x: toPos.x, y: toPos.y },
      fromDims,
      toDims,
      fromSide,
      toSide,
      allNodes,
      edgeIndex: edgeIndex++,
      branch,
      branchColor,
      branchLabel,
      isFromDecision: fromTask?.nodeType === 'decision',
      // Edges touching a milestone must always use the computed path so they terminate
      // on the junction point (ELK routes to the wide footprint box instead).
      isMilestoneEdge: isMilestoneNode(fromTask) || isMilestoneNode(toTask),
      isMilestoneStub: Boolean(isMilestoneStub),
      isNoteStem: Boolean(isNoteStem),
      toAnchorPoint: null,
    });
  });

  const connectionCounts = new Map();

  edgesWithSides.forEach((edge, idx) => {
    const fromKey = `${edge.from}-${edge.fromSide}`;
    const toKey = `${edge.to}-${edge.toSide}`;

    if (!connectionCounts.has(fromKey)) connectionCounts.set(fromKey, []);
    if (!connectionCounts.has(toKey)) connectionCounts.set(toKey, []);

    connectionCounts.get(fromKey).push({ idx, type: 'from' });
    connectionCounts.get(toKey).push({ idx, type: 'to' });
  });

  calculateConnectionOffsets(connectionCounts, connPositions, connDims, edgesWithSides);

  edgesWithSides.forEach(edge => {
    if (edge.fromOffset === undefined) edge.fromOffset = 0;
    if (edge.toOffset === undefined) edge.toOffset = 0;
  });

  return edgesWithSides;
}
